/**
 * useDrawer composable
 * Handles all navigation drawer logic and configuration.
 *
 * @overviewItems Computed drawer items for the overview section.
 * @aboutItems Computed drawer items for the about section.
 * @navigationActivePathRules Defines additional "active" path rules for route highlighting.
 * @openDrawer Opens the drawer and determines its mode (rail or expanded).
 * @closeDrawer Closes the drawer completely.
 * @toggleDrawer Toggles the drawer’s open/rail/closed states.
 * @isPathActive Checks if a given drawer item path is currently active.
 */

//
// Imports
//

// Vuetify
import { useDisplay } from "vuetify";

// App Config / Types
import { drawerConfig } from "../../config/drawer-items";
import type { DrawerItemType } from "../../types/components/common/navigation-drawer";

export const useDrawer = () => {
  //
  // Composables
  //

  // Route
  const route = useRoute();

  // Localization
  const { t } = useI18n();
  const { locale } = useI18n();
  const localePath = useLocalePath();

  // Laboratory Data
  const { fetchName, laboratoryName } = useLaboratory(); // Use default 'laboratory' key for shared state
  const { withAbort } = useAbortableMultiFetch();

  // Breakpoints
  const { mdAndDown, mobile } = useDisplay();

  // Auth — role-based drawer items
  const { isOtpPatient } = useAuth();
  const { visitId } = useOtpVisit();

  //
  // State
  //
  const drawer = useSessionState<boolean>("ui.drawer", true);
  const drawerRailMode = useSessionState<boolean>("ui.drawerRailMode", false);
  // Shared logo state (same key used by login page and app layout)
  const laboratoryLogoUrl = useState<string>("laboratory.logoUrl", () => "");
  const isInitialized = useState<boolean>("drawer.isInitialized", () => false);

  //
  // Computed Properties
  //
  const labName = computed(() => laboratoryName.value || "");

  const overviewItems = computed<DrawerItemType[]>(() => {
    const items = isOtpPatient.value
      ? drawerConfig.otpPatientOverview
      : drawerConfig.corporateOverview;
    return items.map((item) => {
      // For OTP patients, always append an ID so localePath resolves against the
      // dynamic [id] route instead of the non-existent static base path.
      // When visitId is still null (initial mount), use "0" as a harmless placeholder.
      const resolvedPath = isOtpPatient.value ? `${item.path}/${visitId.value ?? "0"}` : item.path;
      return {
        title: t(
          `components.common.navigation-drawer.drawer_sections.overview_section.drawer_items.${item.key}.title`,
        ),
        tooltipText: t(
          `components.common.navigation-drawer.drawer_sections.overview_section.drawer_items.${item.key}.tooltip_text`,
        ),
        icon: item.icon,
        to: localePath(resolvedPath),
      };
    });
  });

  const aboutItems = computed<DrawerItemType[]>(() =>
    drawerConfig.about.map((item) => ({
      title: t(
        `components.common.navigation-drawer.drawer_sections.about_section.drawer_items.${item.key}.title`,
      ),
      tooltipText: t(
        `components.common.navigation-drawer.drawer_sections.about_section.drawer_items.${item.key}.tooltip_text`,
      ),
      icon: item.icon,
      to: localePath(item.path),
    })),
  );

  /**
   * Dev-only drawer items — only populated when LAB_PORTAL_FE_DEV=true.
   * Provides access to developer tools like the UI State Catalogue.
   */
  const config = useRuntimeConfig();
  const devItems = computed<DrawerItemType[]>(() => {
    if (!config.public.DEV) return [];
    return drawerConfig.dev.map((item) => ({
      title: t(
        `components.common.navigation-drawer.drawer_sections.dev_section.drawer_items.${item.key}.title`,
      ),
      tooltipText: t(
        `components.common.navigation-drawer.drawer_sections.dev_section.drawer_items.${item.key}.tooltip_text`,
      ),
      icon: item.icon,
      to: localePath(item.path),
    }));
  });

  const navigationActivePathRules = computed(
    () =>
      ({
        // OTP patient: results item stays active for any /app/otp-patient/results/* route
        ...(isOtpPatient.value && visitId.value
          ? {
              [localePath(`/app/otp-patient/results/${visitId.value}`)]: [
                localePath(`/app/otp-patient/results/${visitId.value}`),
              ],
              [localePath(`/app/otp-patient/balance/${visitId.value}`)]: [
                localePath(`/app/otp-patient/balance/${visitId.value}`),
              ],
            }
          : {}),

        // Corporate: all-visits stays active on patient record and results detail pages
        // Note: /record and /results have no static route (only /[id] children), so we derive
        // the locale-prefixed base from a valid dynamic route and strip the fake ID suffix.
        ...(!isOtpPatient.value
          ? {
              [localePath("/app/corporate/all-visits")]: [
                localePath("/app/corporate/all-visits"),
                localePath("/app/corporate/record/0").replace(/\/0$/, ""),
                localePath("/app/corporate/results/0").replace(/\/0$/, ""),
              ],
            }
          : {}),
      }) as Record<string, string[]>,
  );

  //
  // Methods
  //
  const openDrawer = () => {
    drawer.value = true;
    drawerRailMode.value = !mobile.value && !mdAndDown.value;
  };

  const closeDrawer = () => {
    drawer.value = false;
  };

  const toggleDrawer = () => {
    if (!drawer.value) return openDrawer();
    if (drawerRailMode.value) return (drawerRailMode.value = false);
    return closeDrawer();
  };

  /**
   * Checks if a given drawer item path is currently active.
   * @param {string} itemPath - Target path of the drawer item
   * @returns {boolean} True if the path is active, false otherwise
   */
  const isPathActive = (itemPath: string): boolean => {
    const currentPath = route.path;

    // Direct match
    if (currentPath === itemPath) return true;

    // Match by active path rules
    const extraPaths = navigationActivePathRules.value[itemPath] || [];
    return extraPaths.some((path) => currentPath === path || currentPath.startsWith(`${path}/`));
  };

  /**
   * Load laboratory identity data (name + logo)
   *
   * Purpose:
   * - Fetch laboratory name for drawer header (only if not cached)
   * - Logo is shared state from login page
   * - App layout handles refetching on locale change
   */
  const loadLaboratoryData = async (): Promise<void> => {
    try {
      // Only fetch name if not already cached
      // The app layout is responsible for initial fetch and locale changes
      if (!laboratoryName.value) {
        await withAbort((signal) => fetchName(locale.value, signal, false));
      }
    } catch (err) {
      // Silent failure - drawer can render without logo/name
      console.error("Failed to load laboratory name for drawer:", err);
    }
  };

  /**
   * Initialize drawer with laboratory data
   */
  const initializeDrawer = async (): Promise<void> => {
    if (isInitialized.value) return;

    isInitialized.value = true;
    await loadLaboratoryData();
  };

  // Note: Removed locale watcher to prevent duplicate fetches.
  // The app layout already watches locale and refetches laboratory name.
  // Since we use shared state with the default 'laboratory' key,
  // the drawer automatically gets the updated name from the shared state.

  // DO NOT cleanup blob URLs in drawer - they are shared across the app
  // The app layout is responsible for cleaning up on logout
  // onUnmounted(() => {
  //   if (laboratoryLogoUrl.value?.startsWith('blob:')) {
  //     URL.revokeObjectURL(laboratoryLogoUrl.value);
  //   }
  // });

  return {
    // State
    drawer,
    drawerRailMode,
    laboratoryLogoUrl: readonly(laboratoryLogoUrl),
    labName,

    // Computed
    overviewItems: readonly(overviewItems),
    aboutItems: readonly(aboutItems),
    devItems: readonly(devItems),
    navigationActivePathRules: readonly(navigationActivePathRules),

    // Methods
    toggleDrawer,
    isPathActive,
    initializeDrawer,
  } as const;
};
